import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { buildWorkTime, format, secToTwoDigitsHourMinute, secToTwoDigitsSec } from '../../../utils/date'
import { TaskLabelStatus } from '../../../utils/taskLabelStatus'
import Tips from '../common/Tips'
import PieChart from './PieChart'

interface MemberDetail {
  member_id: number
  member_name: string
  summary: {
    labeling: number
    not_labeling: number
    not_covered: number
  }
  task_type: {
    auto: number
    manual: number
  }
  labeling_status: {
    auto: {
      labeling: number
      not_labeling: number
    }
    manual: {
      labeling: number
      not_labeling: number
    }
  }
  labeling_method: {
    auto: number
    manual: number
  }
  not_covered_services: {
    name: string // Qasee全体での名前
    labeling_target: boolean // 仕分け対象ならtrue
    time: number // 使用時間(秒)
    company_service_group_id: number
  }[]
  view_labeling_board: boolean
  manual_tasks: {
    task_id: number
    time: number
    task_name: string | null
    start_time: string
    end_time: string
    included_untargeted_services: {
      service_name: string
      icon: string | null
      icon_color: string | null
      time: number
    }[]
    label: {
      label_group_id: number
      label_group_name: string
      label_group_color: string
      label_id: number
      label_name: string
      label_color: string
      disabled: boolean
      status: TaskLabelStatus
    } | null
  }[]
}

interface Props {
  member: MemberDetail
  memberClickCallback: (memberId: number) => void
  addCallback: (targetId: number) => void
  isDisplayDetail: boolean
  date: string
  delTaskCallback: (memberId: number, taskId: number) => void
  isDisplayLiteList: boolean
}

const LabelingStatusModule: FC<Props> = (props) => {
  const {
    member,
    memberClickCallback,
    addCallback,
    isDisplayDetail,
    date,
    delTaskCallback,
    isDisplayLiteList,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  // 閾値
  const THRESHOLD = 30.0

  const RESERVED_LABEL_GROUP_ID = 2

  const [isDisplayUntracked, setIsDisplayUntracked] = useState<boolean>(false)
  const [isDisplayManualTasks, setIsDisplayManualTasks] = useState<boolean>(false)

  const [isHigh, setIsHigh] = useState<boolean>(false)

  const notCoveredServices = useMemo(() => {
    if (member.not_covered_services.length > 10) {
      return [...Array(10)].map((_, i) => (member.not_covered_services[i]))
    }
    return member.not_covered_services
  }, [member.not_covered_services])

  const total = useMemo(() => {
    if (!member) return 0
    return member.summary.labeling + member.summary.not_labeling + member.summary.not_covered
  }, [member])

  const isAlertNotCovered = useMemo(() => (
    ((member.summary.not_covered / (member.summary.labeling + member.summary.not_labeling + member.summary.not_covered)) * 100) > THRESHOLD
  ), [])

  const isAlertNotLabeling = useMemo(() => (
    ((member.summary.not_labeling / (member.summary.labeling + member.summary.not_labeling)) * 100) > THRESHOLD
  ), [])

  const displayTime = (sec: number, size = 16, color = "#CCCCCC") => (
    <>
      {secToTwoDigitsHourMinute(sec)}
      <span
        style={{
          fontSize: `${size}px`,
          color: color,
        }}
      >
        {`:${secToTwoDigitsSec(sec)}`}
      </span>
    </>
  )

  const bar = (list: {name: string, color: string, value: number}[], width?: number, displayTitle?: boolean) => {
    const sum = list.reduce((prev, val) => prev + val.value, 0)
    return (
      <div
        className="task_status_bar_wrap"
        style={{
          width: "70%",
          height: "80px",
          display: "block",
        }}
      >
        {
          sum ? (
            <>
              {
                list.map((val) => (
                  <div
                    key={uniqueId()}
                    className="task_status_bar"
                    style={{
                      backgroundColor: val.color,
                      height: `${((val.value / sum) * 100).toFixed(2)}%`,
                    }}
                  >
                    {
                      (Math.round((val.value / sum) * 1000) / 10) > 25 && (
                        <div
                          className="text"
                          style={{
                            color: val.color,
                          }}
                        >
                          {displayTitle ? val.name : displayTime(val.value, 10, val.color)}
                        </div>
                      )
                    }
                    <div className="tip">
                      <div className="title">
                        {val.name}
                      </div>
                      <div className="value">
                        {displayTime(val.value, 12)}
                      </div>
                    </div>
                  </div>
                ))
              }
            </>
          ) : (
            <>
              <div
                className="task_status_bar"
                style={{
                  backgroundColor: "#CCCCCC",
                  height: "80px",
                }}
              >
                <div
                  className="text"
                  style={{
                    color: "#CCCCCC",
                  }}
                >
                  NO DATA
                </div>
                <div className="tip">
                  <div className="title">
                    データがありません
                  </div>
                </div>
              </div>
            </>
          )
        }
      </div>
    )
  }

  const clickDetailUntracked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsHigh((window.innerHeight / 2) > event.clientY)
    setIsDisplayUntracked(!isDisplayUntracked)
  }

  const clickDetailManualTasks = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsHigh((window.innerHeight / 2) > event.clientY)
    setIsDisplayManualTasks(!isDisplayManualTasks)
  }

  const clickRow = (targetId: number) => {
    addCallback(targetId)
  }

  const notLabelingBlock = () => (
    <>
      <div className={`block not_labeling ${isAlertNotLabeling ? "check" : ""}`}>
        <div className="title">
          未仕分け
        </div>
        <div className="value">
          {displayTime(member.summary.not_labeling, 12)}
        </div>
      </div>
    </>
  )

  const notCoveredBlock = () => (
    <>
      <div className={`block not_covered ${isAlertNotCovered ? "check" : ""}`}>
        <div className="title">
          仕分け対象外
        </div>
        <div className="value">
          {displayTime(member.summary.not_covered, 12)}
        </div>
        <div>
          <button
            className="min_button"
            type="button"
            onClick={(e) => clickDetailUntracked(e)}
          >
            仕分け対象外を確認
          </button>
          {
            isDisplayUntracked && (
              <>
                <div className={`untracked_modal_block${isHigh ? " high" : ""}`}>
                  <div className="title_area">
                    対象外の作業
                    {
                      member.not_covered_services.length > 10 && (
                        <div className="description">
                          上位10件を表示しています
                        </div>
                      )
                    }
                  </div>
                  {
                    notCoveredServices.length ? (
                      <>
                        {
                          notCoveredServices.map((service) => (
                            <React.Fragment key={uniqueId()}>
                              {
                                login.role_permission?.task_project_setting ? (
                                  <Tips
                                    description={`[${service.name}]${service.labeling_target ? "は設定済みです" : "を設定します"}`}
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <div
                                        className={`row${(!service.labeling_target) ? " clickable" : ""}`}
                                        onClick={() => !service.labeling_target && clickRow(service.company_service_group_id)}
                                        role="none"
                                      >
                                        {
                                          service.labeling_target ? (
                                            <div className="label">
                                              設定済み
                                            </div>
                                          ) : (
                                            <div className="label no_target">
                                              未設定
                                            </div>
                                          )
                                        }
                                        <div className="name">
                                          {service.name}
                                        </div>
                                        <div>
                                          {buildWorkTime(service.time)}
                                        </div>
                                      </div>
                                    </>
                                  </Tips>
                                ) : (
                                  <div className="row">
                                    <Tips
                                      description="設定はプロジェクト設定権限が必要です"
                                      className=""
                                      icon={false}
                                    >
                                      <div className="label registered">
                                        { service.labeling_target ? "設定済み" : "未設定" }
                                      </div>
                                    </Tips>
                                    <div className="name">
                                      {service.name}
                                    </div>
                                    <div>
                                      {buildWorkTime(service.time)}
                                    </div>
                                  </div>
                                )
                              }
                            </React.Fragment>
                          ))
                        }
                        <div className="text">
                          設定後からQaseeClientで計測されます
                        </div>
                      </>
                    ) : (
                      <div className="no_data">
                        設定できる対象外の作業はありません
                      </div>
                    )
                  }
                </div>
                <div className="close_screen gray" role="presentation" onClick={() => setIsDisplayUntracked(false)} />
              </>
            )
          }
        </div>
      </div>
    </>
  )

  const untrackedModalBlock = (memberDetail: MemberDetail) => (
    <>
      <div className={`untracked_modal_block${isHigh ? " high" : ""}`}>
        <div className="title_area">
          対象外の作業
          {
            memberDetail.not_covered_services.length > 10 && (
              <div className="description">
                上位10件を表示しています
              </div>
            )
          }
        </div>
        {
          notCoveredServices.length ? (
            <>
              {
                notCoveredServices.map((service) => (
                  <React.Fragment key={uniqueId()}>
                    {
                      login.role_permission?.task_project_setting ? (
                        <Tips
                          description={`[${service.name}]${service.labeling_target ? "は設定済みです" : "を設定します"}`}
                          className=""
                          icon={false}
                        >
                          <>
                            <div
                              className={`row${(!service.labeling_target) ? " clickable" : ""}`}
                              onClick={() => !service.labeling_target && clickRow(service.company_service_group_id)}
                              role="none"
                            >
                              {
                                service.labeling_target ? (
                                  <div className="label">
                                    設定済み
                                  </div>
                                ) : (
                                  <div className="label no_target">
                                    未設定
                                  </div>
                                )
                              }
                              <div className="name">
                                {service.name}
                              </div>
                              <div>
                                {buildWorkTime(service.time)}
                              </div>
                            </div>
                          </>
                        </Tips>
                      ) : (
                        <div className="row">
                          <Tips
                            description="設定はプロジェクト設定権限が必要です"
                            className=""
                            icon={false}
                          >
                            <div className="label registered">
                              { service.labeling_target ? "設定済み" : "未設定" }
                            </div>
                          </Tips>
                          <div className="name">
                            {service.name}
                          </div>
                          <div>
                            {buildWorkTime(service.time)}
                          </div>
                        </div>
                      )
                    }
                  </React.Fragment>
                ))
              }
              <div className="text">
                設定後からQaseeClientで計測されます
              </div>
            </>
          ) : (
            <div className="no_data">
              設定できる対象外の作業はありません
            </div>
          )
        }
      </div>
      <div className="close_screen gray" role="presentation" onClick={() => setIsDisplayUntracked(false)} />
    </>
  )

  const manualTaskModalBlock = (memberDetail: MemberDetail) => (
    <>
      <div className={`manual_task_modal_block${isHigh ? " high" : ""}`}>
        <div className="title_area">
          {`${format(date, "YYYY年M月D日")} ${memberDetail.member_name} 手動登録作業データ一覧`}
        </div>
        <div className="row_wrap">
          {
            memberDetail.manual_tasks.length ? (
              <>
                {
                  memberDetail.manual_tasks.map((manualTask) => (
                    <div
                      key={uniqueId()}
                      className="row"
                    >
                      <div className="task_detail">
                        <div className="detail_title">
                          {manualTask.task_name}
                        </div>
                        <div className="status_area">
                          {
                            manualTask.label?.label_group_id && manualTask.label?.label_group_id > RESERVED_LABEL_GROUP_ID ? (
                              <div className="label">
                                {`${manualTask.label.label_group_name}:${manualTask.label?.label_name}`}
                              </div>
                            ) : (
                              <>
                                {
                                  manualTask.label?.label_group_id == null && (
                                    <div className="label red">
                                      未仕分け
                                    </div>
                                  )
                                }
                                {
                                  manualTask.label?.label_group_id === 0 && (
                                    <div className="label other">
                                      プロジェクトに属さない作業データ
                                    </div>
                                  )
                                }
                                {
                                  manualTask.label?.label_group_id === 1 && (
                                    <div className="label break">
                                      休憩
                                    </div>
                                  )
                                }
                                {
                                  manualTask.label?.label_group_id === 2 && (
                                    <div className="label not_working">
                                      業務外
                                    </div>
                                  )
                                }
                              </>
                            )
                          }
                          <div>
                            {buildWorkTime(manualTask.time)}
                          </div>
                          <button
                            type="button"
                            className="help del"
                            onClick={() => delTaskCallback(member.member_id, manualTask.task_id)}
                            style={{
                              padding: "0",
                              fontSize: "12px",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                          </button>
                        </div>
                      </div>
                      {
                        !!manualTask.included_untargeted_services.length && (
                          <div className="manual_task_area">
                            <div className="text">
                              以下の対象外アプリ作業が含まれています
                            </div>
                            {
                              manualTask.included_untargeted_services.map((v) => (
                                <div
                                  className="manual_task_row"
                                  key={uniqueId()}
                                >
                                  <div className="detail">
                                    └
                                    <div
                                      className="icon_area"
                                      style={{
                                        color: v.icon_color || (v.service_name ? "#008800" : "#FF0000"),
                                      }}
                                    >
                                      {
                                        v.icon ? (
                                          <FontAwesomeIcon
                                            icon={[
                                              v.icon.split(" ")[0] as IconPrefix,
                                              v.icon.split(" ")[1] as IconName,
                                            ]}
                                            fixedWidth
                                          />
                                        ) : (
                                          <FontAwesomeIcon
                                            icon={
                                              v.service_name
                                                ? ["fas", "check-circle"]
                                                : ["fas", "exclamation-triangle"]
                                            }
                                            fixedWidth
                                          />
                                        )
                                      }
                                    </div>
                                    {v.service_name}
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        )
                      }
                    </div>
                  ))
                }
              </>
            ) : (
              <div className="no_data">
                手動仕分けはありません
              </div>
            )
          }
        </div>
      </div>
      <div className="close_screen gray" role="presentation" onClick={() => setIsDisplayManualTasks(false)} />
    </>
  )

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isDisplayUntracked || isDisplayManualTasks) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isDisplayUntracked, isDisplayManualTasks])

  return (
    <>
      {
        isDisplayLiteList ? (
          <>
            <div className="task_status_list_detail">
              <div className="task_status_list_name_area">
                <div className="user_name">
                  {member.member_name}
                </div>
                {
                  member.view_labeling_board && (
                    <div
                      onClick={() => memberClickCallback(member.member_id)}
                      role="none"
                      className="task_link"
                    >
                      {"この日の仕分けを見る >"}
                    </div>
                  )
                }
              </div>
              <div className="task_status_list_data_area">
                {
                  total ? (
                    <>
                      <div className="left_area">
                        <div className="detail_area">
                          <div className="detail_block">
                            <div
                              className="label"
                              style={{
                                backgroundColor: "#119555",
                                color: "#FFFFFF",
                              }}
                            >
                              仕分け済み
                            </div>
                            <div>
                              {displayTime(member.summary.labeling, 12)}
                            </div>
                          </div>
                          <div className="detail_block">
                            <div
                              className="label"
                              style={{
                                backgroundColor: "#c61616",
                                color: "#FFFFFF",
                              }}
                            >
                              未仕分け
                            </div>
                            <div>
                              {displayTime(member.summary.not_labeling, 12)}
                            </div>
                          </div>
                        </div>
                        <div className="bar_area">
                          <Tips
                            // eslint-disable-next-line max-len
                            description={`仕分け済み:${Math.round((member.summary.labeling / (member.summary.labeling + member.summary.not_labeling)) * 1000) / 10}%%br%(${buildWorkTime(member.summary.labeling)})`}
                            className=""
                            icon={false}
                            style={{
                              width: `${((member.summary.labeling / (member.summary.labeling + member.summary.not_labeling)) * 1000) / 10}%`,
                            }}
                          >
                            <div
                              className="bar"
                              style={{
                                backgroundColor: "#0D9554",
                                color: "#FFFFFF",
                              }}
                            >
                              {
                                (member.summary.labeling / (member.summary.labeling + member.summary.not_labeling)) * 100 > 6 && (
                                  <>
                                    {/* eslint-disable-next-line max-len */}
                                    {`${Math.round((member.summary.labeling / (member.summary.labeling + member.summary.not_labeling)) * 1000) / 10}%`}
                                  </>
                                )
                              }
                            </div>
                          </Tips>
                          <Tips
                            // eslint-disable-next-line max-len
                            description={`未仕分け:${Math.round((member.summary.not_labeling / (member.summary.labeling + member.summary.not_labeling)) * 1000) / 10}%%br%(${buildWorkTime(member.summary.not_labeling)})`}
                            className=""
                            icon={false}
                            style={{
                              width: `${(member.summary.not_labeling / (member.summary.labeling + member.summary.not_labeling)) * 100}%`,
                            }}
                          >
                            <div
                              className="bar"
                              style={{
                                backgroundColor: "#c61616",
                                color: "#FFFFFF",
                              }}
                            >
                              {
                                (member.summary.not_labeling / (member.summary.labeling + member.summary.not_labeling)) * 100 > 6 && (
                                  <>
                                    {/* eslint-disable-next-line max-len */}
                                    {`${Math.round((member.summary.not_labeling / (member.summary.labeling + member.summary.not_labeling)) * 1000) / 10}%`}
                                  </>
                                )
                              }
                            </div>
                          </Tips>
                        </div>
                      </div>
                      <div className="right_area">
                        <div className="detail_block">
                          <div
                            className="label"
                            style={{
                              backgroundColor: "#3370CC",
                              color: "#FFFFFF",
                            }}
                          >
                            手動登録作業データ件数
                          </div>
                          {member.manual_tasks.length}
                          件
                          <button
                            type="button"
                            onClick={(e) => clickDetailManualTasks(e)}
                          >
                            詳細を確認
                          </button>
                          {isDisplayManualTasks && manualTaskModalBlock(member)}
                        </div>
                        <div className="detail_block">
                          <div
                            className="label"
                            style={{
                              backgroundColor: "#f8f8f8",
                              color: "#333333",
                              border: "solid 1px #333333",
                            }}
                          >
                            仕分け対象外
                          </div>
                          <div>
                            {displayTime(member.summary.not_covered, 12)}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => clickDetailUntracked(e)}
                          >
                            仕分け対象外を確認
                          </button>
                          {isDisplayUntracked && untrackedModalBlock(member)}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="no_data">
                        データがありません
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`task_status_card ${(isAlertNotCovered || isAlertNotLabeling) ? "check" : ""}`}>
              <div className="header_area">
                <div className="name">
                  {member.member_name}
                </div>
                <div
                  onClick={() => memberClickCallback(member.member_id)}
                  role="none"
                  className="link"
                >
                  {"この日の仕分けを見る >"}
                </div>
              </div>
              <div className="contents_area">
                {
                  total ? (
                    <>
                      {
                        isDisplayDetail ? (
                          <>
                            <div className="detail_area">
                              <div className="left_area">
                                <div className="bar_area">
                                  {
                                    bar(
                                      [
                                        {
                                          name: "PCログからの自動計測",
                                          value: member.task_type.auto,
                                          color: "#4472BA",
                                        },
                                        {
                                          name: "PC未使用からの手動登録",
                                          value: member.task_type.manual,
                                          color: "#832AB7",
                                        },
                                      ],
                                      100,
                                      false,
                                    )
                                  }
                                  <div className="title_area">
                                    作業データ分類
                                  </div>
                                  {/* bg_up */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "5px",
                                      right: "0px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "1px",
                                      right: "-8px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                      transform: "rotate(-55deg)",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "-3px",
                                      right: "-16px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                  {/* bg_down */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "5px",
                                      right: "0px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "1px",
                                      right: "-8px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                      transform: "rotate(55deg)",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-3px",
                                      right: "-16px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                  <div className="legend_area">
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#4472BA",
                                        }}
                                      >
                                        <img
                                          src="/static/images/task/icon_auto.png"
                                          alt=""
                                          style={{
                                            width: "14px",
                                          }}
                                        />
                                      </div>
                                      自動計測
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#832AB7",
                                        }}
                                      >
                                        <img
                                          src="/static/images/task/icon_manual.png"
                                          alt=""
                                          style={{
                                            width: "14px",
                                          }}
                                        />
                                      </div>
                                      手動登録
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="center_area">
                                <div className="bar_area">
                                  {
                                    bar(
                                      [
                                        {
                                          name: "仕分け済み",
                                          color: "#0D9554",
                                          value: member.labeling_status.auto.labeling,
                                        },
                                        {
                                          name: "未仕分け",
                                          color: "#c61616",
                                          value: member.labeling_status.auto.not_labeling,
                                        },
                                      ],
                                      100,
                                      false,
                                    )
                                  }
                                  <div className="title_area">
                                    仕分け状況
                                  </div>
                                  {/* bg_up */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "5px",
                                      right: "0px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "1px",
                                      right: "-8px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                      transform: "rotate(-55deg)",
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "-3px",
                                      right: "-16px",
                                      width: "10px",
                                      height: "1px",
                                      backgroundColor: "#999999",
                                    }}
                                  />
                                </div>
                                <div className="bar_area">
                                  {
                                    bar(
                                      [
                                        {
                                          name: "仕分け済み",
                                          color: "#0D9554",
                                          value: member.labeling_status.manual.labeling,
                                        },
                                        {
                                          name: "未仕分け",
                                          color: "#c61616",
                                          value: member.labeling_status.manual.not_labeling,
                                        },
                                      ],
                                      100,
                                      false,
                                    )
                                  }
                                  <div className="legend_area">
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#0D9554",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'tag']} rotation={90} fixedWidth />
                                      </div>
                                      仕分け済み
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#c61616",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'tag']} rotation={90} fixedWidth />
                                      </div>
                                      未仕分け
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="right_area">
                                <div className="bar_area">
                                  {
                                    bar(
                                      [
                                        {
                                          name: "自動仕分け",
                                          value: member.labeling_method.auto,
                                          color: "#00CCCC",
                                        },
                                        {
                                          name: "手動仕分け",
                                          value: member.labeling_method.manual,
                                          color: "#818181",
                                        },
                                      ],
                                      100,
                                      false,
                                    )
                                  }
                                  <div className="title_area">
                                    仕分け方法
                                  </div>
                                  <div className="legend_area">
                                    <div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#00CCCC",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'robot']} fixedWidth />
                                      </div>
                                      自動仕分け
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          transform: "rotate(-25deg)",
                                          fontSize: "12px",
                                          color: "#818181",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'hand-point-right']} fixedWidth />
                                      </div>
                                      手動仕分け
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="summary_area">
                              <div className="left_area">
                                <div className="block_wrapper">
                                  <div className="block manual">
                                    <div className="title">
                                      手動登録作業データ件数
                                    </div>
                                    <div>
                                      {`${member.manual_tasks.length}件`}
                                    </div>
                                    <div>
                                      <button
                                        className="min_button"
                                        type="button"
                                        onClick={(e) => clickDetailManualTasks(e)}
                                      >
                                        詳細を確認
                                      </button>
                                      {isDisplayManualTasks && manualTaskModalBlock(member)}
                                    </div>
                                  </div>
                                  {
                                    isAlertNotCovered ? (
                                      <>
                                        <Tips
                                          description="対象外の作業が多く、正確にコストが算出できない恐れがあります。%br%業務仕分け対象のアプリケーションを見直してみましょう。"
                                          className="height_auto"
                                          icon={false}
                                          width={280}
                                        >
                                          {notCoveredBlock()}
                                        </Tips>
                                      </>
                                    ) : (
                                      <>
                                        {notCoveredBlock()}
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                              <div className="right_area">
                                <div className="graph_area">
                                  <PieChart
                                    list={[
                                      {
                                        label: "",
                                        value: member.summary.labeling,
                                        color: "#0D9554",
                                      },
                                      {
                                        label: "",
                                        value: member.summary.not_labeling,
                                        color: "#c61616",
                                      },
                                    ]}
                                    chartSize={180}
                                  />
                                  <div className="block_area">
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "4px",
                                        alignItems: "flex-end",
                                      }}
                                    >
                                      {
                                        isAlertNotLabeling ? (
                                          <>
                                            <Tips
                                              description="業務仕分けが少ないため正確にコストが算出できません。%br%ユーザーに仕分け作業を指示するか、自動仕分けの設定を見直してみましょう。"
                                              className="height_auto"
                                              icon={false}
                                              width={280}
                                            >
                                              {notLabelingBlock()}
                                            </Tips>
                                          </>
                                        ) : (
                                          <>
                                            {notLabelingBlock()}
                                          </>
                                        )
                                      }
                                      <div className="block labeling">
                                        <div className="title">
                                          仕分け済み
                                        </div>
                                        <div className="value">
                                          {displayTime(member.summary.labeling, 12)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )
                      }
                    </>
                  ) : (
                    <>
                      <div className="no_data">
                        データがありません
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          </>
        )
      }
    </>
  )
}

export default LabelingStatusModule
